/* eslint-disable */
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { db, auth } from "../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  updateDoc,
  doc,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";

const Particle = ({ index }) => {
  const colors = [
    "#FFD700",
    "#FF6B9D",
    "#00D4FF",
    "#7C3AED",
    "#FF4500",
    "#00FF88",
  ];
  const color = colors[index % colors.length];
  const size = Math.random() * 6 + 3;
  const x = Math.random() * 100;
  const delay = Math.random() * 2;
  const duration = Math.random() * 3 + 2;

  return (
    <motion.div
      className="absolute rounded-full pointer-events-none"
      style={{
        width: size,
        height: size,
        backgroundColor: color,
        left: `${x}%`,
        top: "110%",
        boxShadow: `0 0 ${size * 2}px ${color}`,
      }}
      animate={{
        top: ["-10%"],
        rotate: [0, 360],
        opacity: [1, 0.8, 0],
        x: [0, (Math.random() - 0.5) * 80],
      }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: "easeOut",
      }}
    />
  );
};

const UploadValidationModal = ({ isOpen, onClose, setIsOpen, onSuccess }) => {
  const user = useSelector((state) => state.AccountReducer?.user || null);
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const getUserIdentifiers = () => {
    const accountUser = user || {};
    return { accountUser };
  };
  
  const handleClose = () => {
    setSelectedImage(null);
    setPreviewUrl(null);
    setIsUploading(false);
    setUploadProgress(0);
    setErrorMessage("");
    setSuccessMessage("");
    if (onClose) onClose();
    if (setIsOpen) setIsOpen(false);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setErrorMessage("");
      setSuccessMessage("");
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const convertToWebp = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = () => {
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          canvas.toBlob(
            (blob) => {
              if (blob) resolve(blob);
              else reject(new Error("WebP conversion failed"));
            },
            "image/webp",
            0.8,
          );
        };
        img.onerror = reject;
        img.src = reader.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const findExistingDhdDoc = async ({ accountUser }) => {
    const matchesAccount = (docData) => {
      const docUser = docData?.user || {};
      if (docUser && accountUser && docUser === accountUser) return true;
      return false;
    };

    if (accountUser?.id) {
      const byUserId = query(collection(db, "dhd_form"), where("user", "==", accountUser));
      const snapshot = await getDocs(byUserId);
      if (!snapshot.empty) {
        const docMatch = snapshot.docs.find((d) => matchesAccount(d.data())) || snapshot.docs[0];
        if (docMatch) return docMatch;
      }
    }

    const latest = query(
      collection(db, "dhd_form"),
      orderBy("createdAt", "desc"),
      limit(1),
    );
    const latestSnap = await getDocs(latest);
    if (!latestSnap.empty) {
      const docMatch = latestSnap.docs.find((d) => matchesAccount(d.data())) || latestSnap.docs[0];
      if (docMatch) return docMatch;
    }
    return null;
  };

  const handleUpload = async () => {
    if (!selectedImage) return;

    setIsUploading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const { accountUser } = getUserIdentifiers();

      let fileToUpload = selectedImage;
      if (selectedImage.type !== "image/gif") {
        fileToUpload = await convertToWebp(selectedImage);
      }

      const dataImage = new FormData();
      dataImage.append("upload", fileToUpload, selectedImage.name);

      const response = await axios.post(
        "https://octopus-app-i4ylg.ondigitalocean.app/upload",
        dataImage,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round(
                (progressEvent.loaded / progressEvent.total) * 100,
              );
              setUploadProgress(progress);
            }
          },
        },
      );

      const downloadUrl = response?.data?.url;
      if (!downloadUrl) {
        throw new Error("No URL returned from upload endpoint");
      }

      const existingDoc = await findExistingDhdDoc({
        accountUser,
      });

      const payload = {
        receiptImageUrl: downloadUrl,
        user: accountUser || null,
      };

      if (existingDoc) {
        const targetRef = doc(db, "dhd_form", existingDoc.id);
        await updateDoc(targetRef, payload);
      } else {
        await addDoc(collection(db, "dhd_form"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }

      setIsUploading(false);
      setSuccessMessage("تم حفظ الوصل بنجاح ✅");
      onSuccess?.(downloadUrl);
      handleClose();
    } catch (error) {
      console.error("❌ Error uploading receipt:", error);
      setIsUploading(false);
      setErrorMessage(t("validationModal.errorUpload"));
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[10002] flex items-center justify-center px-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 22, stiffness: 280 }}
            className="relative w-full max-w-md rounded-3xl overflow-hidden"
            style={{
              background: "linear-gradient(145deg, #ffffff 0%, #fffdf5 50%, #fff9e6 100%)",
              border: "1px solid rgba(255, 193, 7, 0.3)",
              boxShadow: "0 50px 100px rgba(0,0,0,0.25), 0 0 60px rgba(255, 165, 0, 0.12)",
            }}
          >
            <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
              {Array.from({ length: 12 }).map((_, i) => (
                <Particle key={i} index={i} />
              ))}
            </div>

            <div
              className="relative overflow-hidden text-center"
              style={{
                background: "linear-gradient(135deg, #1a0a00 0%, #3d1a00 30%, #5c2800 50%, #3d1a00 70%, #1a0a00 100%)",
                borderBottom: "1px solid rgba(255, 215, 0, 0.25)",
              }}
            >
              <div className="relative z-10 py-6 px-6">
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ repeat: Infinity, duration: 3 }}
                  className="text-5xl mb-2"
                >
                  🧾
                </motion.div>
                <h2
                  className="text-2xl font-black"
                  style={{
                    background: "linear-gradient(135deg, #FFD700 0%, #FFA500 40%, #FFD700 70%, #FFFACD 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {t("validationModal.title")}
                </h2>
                <p className="text-amber-200/80 text-xs mt-1">
                  {t("validationModal.subtitle")}
                </p>
              </div>

              <motion.button
                whileHover={{ scale: 1.15, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleClose}
                className="absolute top-4 left-4 w-8 h-8 flex items-center justify-center rounded-full font-black text-base transition-all"
                style={{
                  background: "rgba(0,0,0,0.1)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  color: "rgba(255,255,255,0.9)",
                }}
              >
                ✕
              </motion.button>
            </div>

            <div className="p-6 relative z-10">
              <div className="space-y-4">
                <div
                  className="relative rounded-2xl border-2 border-dashed p-4 transition-all"
                  style={{
                    borderColor: previewUrl ? "rgba(34, 197, 94, 0.5)" : "rgba(255, 165, 0, 0.3)",
                    background: previewUrl ? "rgba(34, 197, 94, 0.05)" : "rgba(255, 165, 0, 0.02)",
                  }}
                >
                  {previewUrl ? (
                    <div className="relative">
                      <img
                        src={previewUrl}
                        alt={t("Products.Table.headers.preview")}
                        className="w-full h-48 object-contain rounded-xl"
                      />
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => {
                      setSelectedImage(null);
                      setPreviewUrl(null);
                    }}
                    className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center text-sm font-black shadow-lg"
                  >
                    ✕
                  </motion.button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center h-48 cursor-pointer">
                      <motion.div
                        animate={{ y: [0, -5, 0] }}
                        transition={{ repeat: Infinity, duration: 2 }}
                        className="text-4xl mb-2"
                      >
                        📎
                      </motion.div>
                      <span className="text-sm font-bold text-gray-600 mb-1">
                        {t("validationModal.uploadPrompt")}
                      </span>
                      <span className="text-xs text-gray-400">
                        {t("validationModal.uploadSubtext")}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>

                <div
                  className="rounded-xl p-4 space-y-2"
                  style={{
                    background: "linear-gradient(135deg, rgba(34,197,94,0.08), rgba(16,185,129,0.03))",
                    border: "1px solid rgba(34, 197, 94, 0.2)",
                  }}
                >
                  <p className="text-xs font-black text-emerald-700 mb-2 flex items-center gap-2">
                    <span>💡</span>
                    {t("validationModal.noteTitle")}
                  </p>
                  <p className="text-xs text-gray-600">
                    {t("validationModal.noteText")}
                  </p>
                </div>

                {errorMessage && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-xs font-bold text-red-700">
                    {t("validationModal.errorUpload")}
                  </div>
                )}
                {successMessage && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs font-bold text-emerald-700">
                    {t("validationModal.successUpload")}
                  </div>
                )}

                {isUploading && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-2"
                  >
                    <div className="flex justify-between text-xs">
                      <span className="text-amber-600 font-bold">{t("validationModal.uploading")}</span>
                      <span className="text-amber-600 font-bold">{uploadProgress}%</span>
                    </div>
                    <div className="h-2 bg-amber-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${uploadProgress}%` }}
                        className="h-full rounded-full"
                        style={{
                          background: "linear-gradient(90deg, #FFD700, #FFA500)",
                        }}
                      />
                    </div>
                  </motion.div>
                )}

                <div className="space-y-2">
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleUpload}
                    disabled={!selectedImage || isUploading}
                    className="w-full relative py-4 rounded-xl font-black text-base overflow-hidden transition-all"
                    style={{
                      background: selectedImage && !isUploading
                        ? "linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF8C00 100%)"
                        : "rgba(0,0,0,0.07)",
                      border: selectedImage && !isUploading
                        ? "none"
                        : "1.5px solid rgba(0,0,0,0.1)",
                      color: selectedImage && !isUploading
                        ? "#1a0a00"
                        : "rgba(0,0,0,0.3)",
                      cursor: selectedImage && !isUploading ? "pointer" : "not-allowed",
                    }}
                  >
                    {isUploading ? (
                      <span className="flex items-center justify-center gap-2">
                        <motion.span
                          animate={{ rotate: 360 }}
                          transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                        >
                          ⏳
                        </motion.span>
                        {t("validationModal.uploading")}
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <span>✅</span>
                        {t("validationModal.uploadCta")}
                      </span>
                    )}
                    
                    {selectedImage && !isUploading && (
                      <motion.div
                        className="absolute inset-0"
                        animate={{ x: ["-100%", "100%"] }}
                        transition={{
                          repeat: Infinity,
                          duration: 1.8,
                          ease: "linear",
                        }}
                        style={{
                          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)",
                          width: "40%",
                        }}
                      />
                    )}
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleClose}
                    className="w-full py-3 rounded-xl font-bold text-sm transition-all"
                    style={{
                      background: "rgba(0,0,0,0.04)",
                      border: "1px solid rgba(0,0,0,0.1)",
                      color: "rgba(0,0,0,0.6)",
                    }}
                  >
                    {t("validationModal.cancel")}
                  </motion.button>
                </div>

                <p className="text-[9px] text-center text-gray-400">
                  {t("validationModal.reviewTime")}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default UploadValidationModal;